import * as React from 'react';
import useDebouncedCallback from 'hooks/useDebouncedCallback';
import { notify } from 'utils';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Layout from "../../../Components/layouts";
import Typography from '@material-ui/core/Typography';
import GenericTable from '../../../Components/GenericTable';
import { CustomerServices } from '../../../Services/CustomerServices';
import { Pagination } from '@material-ui/lab';
import OutlinedButton from '../../../Components/OutlinedButton';
import SearchBoxInput from '../../../Components/SearchBoxInput';
import { useHistory, useLocation } from 'react-router';
import commonStyles from 'theme/commonStyles';
import { customerColumns } from './customerColumns';

const customerServices = new CustomerServices();

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        background: '#FFFFFF',
        boxShadow: "0px 4px 30px rgba(12, 41, 84, 0.1)",
        borderRadius: 5,
        width: '100%',
        boxSizing: 'border-box',
        [theme.breakpoints.up('sm')]: {
            minHeight: 180,
        },
    },
    cardDetails: {
        display: 'flex',
        flexDirection: 'column',
    },
    content: {
        flex: '1 0 auto',
    },
    cover: {
        width: '100%',
        maxWidth: 174,
        height: 'auto',
        maxHeight: 132,
        paddingTop: 17,
        marginTop: 16,
        marginLeft: 0,
        borderRadius: 5,
        objectFit: 'contain',
        [theme.breakpoints.up('sm')]: {
            marginTop: 25,
            marginLeft: 20,
        },
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
        paddingLeft: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    cardTitle: {
        color: "#808080"
    },
    cardSubTitle: {
        fontFamily: 'Inter',
        fontStyle: 'normal',
        color: "#3D7DDD",
    },
    pageTitle: {
        marginBottom: theme.spacing(3),
        wordBreak: 'break-word',
    },
    searchBox: {
        border: "1px solid #808080",
        borderRadius: 25,
        padding: '7px 15px'
    },
    createCustomerBtn: {
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    }
}));

const Customers = () => {

    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const commonClasses = commonStyles();

    const [filterState, setFilterState] = React.useState(() => ({
        page: 1,
        search: new URLSearchParams(location.search).get('search') || '',
    }));
    const [administratorList, setAdministratorList] = React.useState([]);
    const [pagination, setPagination] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const requestId = React.useRef(0);

    const getAllCustomer = React.useCallback(async (queryData) => {
        const currentRequest = ++requestId.current;
        setLoading(true);
        try {
            const query = `search=${encodeURIComponent(queryData.search)}&page=${queryData.page}`;
            const customersList = await customerServices.getAllCustomer(`main/customerlist?${query}`);
            if (currentRequest !== requestId.current) return;
            setAdministratorList(customersList?.results || []);
            setPagination(customersList?.pagination || null);
        } catch (error) {
            if (currentRequest === requestId.current) {
                notify('error', 'Could not load customers. Please try again.');
            }
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    }, []);
    const searchLater = useDebouncedCallback(getAllCustomer);

    const handlePageChange = (evt, page) => {
        searchLater.cancel();
        const next = { ...filterState, page };
        setFilterState(next);
        getAllCustomer(next);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        searchLater.cancel();
        const next = { ...filterState, page: 1 };
        setFilterState(next);
        getAllCustomer(next);
    };

    const handleSearchChange = (evt) => {
        const next = { ...filterState, search: evt.target.value, page: 1 };
        setFilterState(next);
        // Ignore responses for the previous input, even while the new debounce is waiting.
        requestId.current += 1;
        setLoading(false);
        searchLater.cancel();
        if (next.search) searchLater(next);
        else getAllCustomer(next);
    };

    const handleItemClick = (row, action) => {
        if (!row) return null;
        history.push(`flowcustomer/${row.uuid}`);
    };

    const handleCreateNew = () => {
        history.push('/flowcustomer');
    };

    React.useEffect(() => {
        getAllCustomer(filterState);
        return () => {
            requestId.current += 1;
            searchLater.cancel();
        };
    // Only the initial URL-derived query loads here; typing and paging have explicit handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalPages = pagination ? Math.ceil(pagination.total_pages) : 1;
    return (
        <Layout>
            <main>
                <div className={commonClasses.contentWrapper}>
                    <Typography variant="h4" className={commonClasses.pageTitle}>Customers</Typography>
                    <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                        <Grid item xs={12} sm={6} md={4}>
                            <SearchBoxInput
                                placeholder="Search name, email, country, partner…"
                                value={filterState.search}
                                handleChange={handleSearchChange}
                                handleSearch={handleSearch}
                            />
                        </Grid>
                        <Grid item xs={12} sm="auto">
                            <OutlinedButton
                                title={"Create Customer"}
                                handleClick={handleCreateNew}
                                align={'right'}
                                className={classes.createCustomerBtn}
                            />
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: 20 }}>
                        <GenericTable
                            columns={customerColumns}
                            applyMinWidth={false}
                            loading={loading}
                            handleItemClick={handleItemClick}
                            list={administratorList}
                        />
                    </Grid>
                    <Grid container style={{ marginTop: 22 }} >
                        {pagination && (
                            <Pagination
                                page={filterState.page}
                                onChange={handlePageChange}
                                className={commonClasses.pagination}
                                count={totalPages}
                                variant="outlined"
                                shape="rounded"
                                siblingCount={2}
                                boundaryCount={2}
                            />
                        )}
                    </Grid>
                </div>
            </main>
        </Layout>
    );
}

export default Customers;
